import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useAuthModal } from './AuthModalContext';

// What the signed-in person has hearted, shared by every heart on the page
// (a service card and the same service's page agree) and the Saved page.
// kind is 'listings' (services) or 'freelancers'. See server/routes/saved.js.
const SavedContext = createContext(null);

const EMPTY = { listings: new Set(), freelancers: new Set() };
const endpoint = (kind, id) => (kind === 'listings' ? `/api/saved/listings/${id}` : `/api/saved/${id}`);

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [ids, setIds] = useState(EMPTY);
  const [error, setError] = useState('');
  const inFlight = useRef(new Set());

  useEffect(() => {
    if (!user?._id) { setIds(EMPTY); return undefined; }
    let cancelled = false;
    fetch('/api/saved/ids', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setIds({ listings: new Set(data.listings), freelancers: new Set(data.freelancers) });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?._id]);

  const setOne = useCallback((kind, id, on) => {
    setIds((prev) => {
      const next = new Set(prev[kind]);
      if (on) next.add(id); else next.delete(id);
      return { ...prev, [kind]: next };
    });
  }, []);

  // Fills or empties the heart straight away, then puts it back if the
  // server refuses (and says so).
  const write = useCallback(async (kind, id, on) => {
    const key = `${kind}:${id}`;
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setError('');
    setOne(kind, id, on);
    try {
      const res = await fetch(endpoint(kind, id), { method: on ? 'POST' : 'DELETE', credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || (on ? 'Could not save that.' : 'Could not remove that.'));
      }
    } catch (err) {
      setOne(kind, id, !on);
      setError(err.message || 'Network error. Please try again.');
    } finally {
      inFlight.current.delete(key);
    }
  }, [setOne]);

  const isSaved = useCallback((kind, id) => !!id && ids[kind].has(String(id)), [ids]);

  const toggle = useCallback((kind, rawId) => {
    const id = String(rawId);
    if (!user) {
      // Sign in first, then finish the save they asked for. Deferred a tick:
      // the modal runs onSuccess while it is still updating its own state.
      openAuthModal({ mode: 'signin', onSuccess: () => setTimeout(() => write(kind, id, true), 0) });
      return;
    }
    write(kind, id, !ids[kind].has(id));
  }, [user, ids, openAuthModal, write]);

  const value = useMemo(() => ({
    isSaved, toggle, error, clearError: () => setError(''), remove: (kind, id) => write(kind, String(id), false),
  }), [isSaved, toggle, error, write]);

  // A failed save puts the heart back; this says why, then clears itself.
  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 4000);
    return () => clearTimeout(t);
  }, [error]);

  return (
    <SavedContext.Provider value={value}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-6 z-[140] flex justify-center px-4">
        {error && (
          <p className="m-0 rounded-full bg-press-ink px-4 py-2 text-sm font-semibold text-press-paper shadow-lg">{error}</p>
        )}
      </div>
    </SavedContext.Provider>
  );
}

export function useSaved() {
  const ctx = useContext(SavedContext);
  if (!ctx) throw new Error('useSaved must be used inside SavedProvider');
  return ctx;
}
